/**
 * @file BillingBillDetailTab.js
 * @description Tab component for managing a client's billing override date, billing type, billing address, and notes.
 */

import {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react';
import Toast from '../../ui/toasts/Toast';
import Spinner from '../../ui/spinners/Spinner';
import styles from '../Billing.module.scss';
import {
    getBillDetail,
    createBillDetail,
    updateBillDetail,
    getClientContacts,
} from '../../../services/clientService';
import UsStatesTerritories from '../../shared/asyncDropdowns/UsStatesTerritories';
import CountryDropdown from '../../shared/asyncDropdowns/CountryDropdown';
import BillingTypeDropdown from '../../shared/asyncDropdowns/BillingTypeDropdown';
import CustomDatePicker from '../../helpers/CustomDatePicker_V2';
import { getText } from '../../../i18n';

const BILLING_CONTACT_ROLE = 'Billing Contact';
const BILLING_ADDRESS_TYPE_ID = 2;

const getInitialFormData = (billDetail, tenantId, fallbackName = '') => ({
    id: billDetail?.id || 0,
    tenantId,
    billingOverrideStartDate: billDetail?.billingOverrideStartDate || null,
    billTypeId: billDetail?.billTypeId || null,
    note: billDetail?.note || '',
    name: billDetail?.name || fallbackName,
    address: {
        id: billDetail?.address?.id || 0,
        tenantId,
        address: billDetail?.address?.address || '',
        address2: billDetail?.address?.address2 || '',
        city: billDetail?.address?.city || '',
        state: billDetail?.address?.state || '',
        zip: billDetail?.address?.zip || '',
        country: billDetail?.address?.country || 'US',
        addressTypeId: BILLING_ADDRESS_TYPE_ID,
    },
});

const formatBillingContact = contact => {
    if (!contact) return '';
    if (typeof contact === 'string') return contact;
    return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
};

const BillingBillDetailTab = forwardRef(
    ({ data, clientId, onRefresh }, ref) => {
        const [formData, setFormData] = useState(() =>
            getInitialFormData(null, clientId, data?.name),
        );
        const [billingContact, setBillingContact] = useState('-');
        const [isLoading, setIsLoading] = useState(true);
        const [errors, setErrors] = useState({});
        const savedSnapshotRef = useRef(null);

        const clearFieldError = fieldName => {
            setErrors(prevErrors => {
                if (!prevErrors[fieldName]) return prevErrors;
                const { [fieldName]: _removed, ...rest } = prevErrors;
                return rest;
            });
        };

        const validateForm = () => {
            const validationErrors = {};
            const requiredFieldMessage = getText('msg_ab90d9d736f9');

            if (!formData.name.trim()) {
                validationErrors.name = requiredFieldMessage;
            }
            if (!formData.billTypeId) {
                validationErrors.billTypeId = requiredFieldMessage;
            }
            if (!formData.address.address.trim()) {
                validationErrors.address = requiredFieldMessage;
            }
            if (!formData.address.city.trim()) {
                validationErrors.city = requiredFieldMessage;
            }
            if (!formData.address.state) {
                validationErrors.state = requiredFieldMessage;
            }
            if (!formData.address.zip.trim()) {
                validationErrors.zip = requiredFieldMessage;
            }
            if (!formData.address.country) {
                validationErrors.country = requiredFieldMessage;
            }

            return validationErrors;
        };

        const isDirty = () =>
            savedSnapshotRef.current !== null &&
            savedSnapshotRef.current !== JSON.stringify(formData);

        const loadBillingContactFromRoles = async () => {
            if (!clientId) return;

            try {
                const contacts = await getClientContacts(clientId);
                const billingContacts = Array.isArray(contacts)
                    ? contacts.filter(
                          contact =>
                              contact.contactRole === BILLING_CONTACT_ROLE,
                      )
                    : [];
                const mainContact =
                    billingContacts.find(contact => contact.isMain) ||
                    billingContacts[0];
                setBillingContact(formatBillingContact(mainContact) || '-');
            } catch (error) {
                console.error('Failed to fetch client contacts', error);
                setBillingContact('-');
            }
        };

        useEffect(() => {
            const loadBillDetail = async () => {
                if (!clientId) {
                    setIsLoading(false);
                    return;
                }

                try {
                    setIsLoading(true);
                    const fetchedBillDetail = await getBillDetail(clientId);
                    const initialFormData = getInitialFormData(
                        fetchedBillDetail,
                        clientId,
                        data?.name,
                    );
                    setFormData(initialFormData);
                    savedSnapshotRef.current = JSON.stringify(initialFormData);

                    // bill-detail returns its own `contact` field; fall back to the
                    // client's contact-role list only when that field is empty.
                    const formattedContact = formatBillingContact(
                        fetchedBillDetail?.contact,
                    );
                    if (formattedContact) {
                        setBillingContact(formattedContact);
                    } else {
                        await loadBillingContactFromRoles();
                    }
                } catch (error) {
                    const fallbackFormData = getInitialFormData(
                        null,
                        clientId,
                        data?.name,
                    );
                    setFormData(fallbackFormData);
                    savedSnapshotRef.current = JSON.stringify(fallbackFormData);
                    await loadBillingContactFromRoles();
                } finally {
                    setIsLoading(false);
                }
            };

            loadBillDetail();
        }, [clientId]);

        const handleNoteChange = e => {
            const { value } = e.target;
            setFormData(prevData => ({
                ...prevData,
                note: value,
            }));
        };

        const handleNameChange = e => {
            const { value } = e.target;
            clearFieldError('name');
            setFormData(prevData => ({
                ...prevData,
                name: value,
            }));
        };

        const handleAddressChange = e => {
            const { name, value } = e.target;
            clearFieldError(name);
            setFormData(prevData => ({
                ...prevData,
                address: {
                    ...prevData.address,
                    [name]: value,
                },
            }));
        };

        const handleDateChange = date => {
            setFormData(prevData => ({
                ...prevData,
                billingOverrideStartDate: date,
            }));
        };

        const handleBillingTypeChange = billTypeId => {
            clearFieldError('billTypeId');
            setFormData(prevData => ({
                ...prevData,
                billTypeId,
            }));
        };

        const handleStateChange = state => {
            clearFieldError('state');
            setFormData(prevData => ({
                ...prevData,
                address: {
                    ...prevData.address,
                    state,
                },
            }));
        };

        const handleCountryChange = country => {
            clearFieldError('country');
            setFormData(prevData => ({
                ...prevData,
                address: {
                    ...prevData.address,
                    country,
                },
            }));
        };

        const validate = () => {
            const validationErrors = validateForm();
            const isValid = Object.keys(validationErrors).length === 0;

            if (!isValid) {
                setErrors(validationErrors);
                Toast.error(getText('msg_37b4b75a809c'));
            }

            return isValid;
        };

        const saveBillDetail = async () => {
            if (!validate()) {
                return false;
            }

            try {
                // `id: 0` means no bill-detail record exists yet (GET always returns
                // an object, even before creation), so it must be a POST, not a PUT.
                if (!formData.id) {
                    await createBillDetail(formData, clientId);
                } else {
                    await updateBillDetail(formData, clientId);
                }

                savedSnapshotRef.current = JSON.stringify(formData);

                if (onRefresh) {
                    onRefresh();
                }

                return true;
            } catch (error) {
                console.error('Failed to save billing details', error);
                const validationErrors = error.response?.data?.errors;

                if (validationErrors && typeof validationErrors === 'object') {
                    Object.values(validationErrors)
                        .flat()
                        .forEach(message => Toast.error(message));
                } else {
                    const errorMessage =
                        error.response?.data?.detail ||
                        error.response?.data?.title ||
                        getText('msg_99a39f7ae753');
                    Toast.error(errorMessage);
                }

                return false;
            }
        };

        useImperativeHandle(ref, () => ({
            save: saveBillDetail,
            isDirty,
            validate,
        }));

        if (isLoading) {
            return <Spinner width={50} className={styles.loadingSpinner} />;
        }

        return (
            <div className="col-12 col-xs-12 col-md-8 col-lg-8 m-auto pb-5 pt-4">
                <div className="row">
                    <div className="col-12 col-md-6 pe-md-3">
                        <div className="form-group mb-3">
                            <label htmlFor="billingOverrideStartDate">
                                {getText('msg_611d622bfd64')}
                            </label>
                            <CustomDatePicker
                                selectedDate={formData.billingOverrideStartDate}
                                onDateChange={handleDateChange}
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-6 ps-md-3">
                        <BillingTypeDropdown
                            selectedBillingType={formData.billTypeId}
                            onBillingTypeChange={handleBillingTypeChange}
                            inlineLabel={false}
                            isRequired
                            className={errors.billTypeId ? 'is-invalid' : ''}
                        />
                    </div>

                    <div className="col-12 col-md-6 pe-md-3">
                        <div className="form-group mb-3">
                            <label htmlFor="name">
                                {getText('msg_12e281345750')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                className={`form-control${errors.name ? ' is-invalid' : ''}`}
                                value={formData.name}
                                onChange={handleNameChange}
                                placeholder={getText('msg_0528c2f09422')}
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-6 ps-md-3">
                        <div className="form-group mb-3">
                            <label>{getText('msg_eca63bd3bf92')}</label>
                            <div className="position-relative mx-2">
                                {billingContact}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 pe-md-2">
                        <div className="form-group mb-3">
                            <label htmlFor="address">
                                {getText('msg_d70f93df5e8f')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                id="address"
                                name="address"
                                className={`form-control${errors.address ? ' is-invalid' : ''}`}
                                value={formData.address.address}
                                onChange={handleAddressChange}
                                placeholder={getText('msg_d06d7b99dd4c')}
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-2 px-md-2">
                        <div className="form-group mb-3">
                            <label htmlFor="address2">
                                {getText('msg_cde4896eead6')}
                            </label>
                            <input
                                type="text"
                                id="address2"
                                name="address2"
                                className="form-control"
                                value={formData.address.address2}
                                onChange={handleAddressChange}
                                placeholder={getText('msg_6793d1e4a929')}
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-4 ps-md-2">
                        <div className="form-group mb-3">
                            <label htmlFor="city">
                                {getText('msg_4271627f4f0b')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                id="city"
                                name="city"
                                className={`form-control${errors.city ? ' is-invalid' : ''}`}
                                value={formData.address.city}
                                onChange={handleAddressChange}
                                placeholder={getText('msg_6b96681c9c88')}
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-4 pe-md-2">
                        <UsStatesTerritories
                            selectedState={formData.address.state}
                            onStateChange={handleStateChange}
                            inlineLabel={false}
                            isRequired
                            className={errors.state ? 'is-invalid' : ''}
                        />
                    </div>

                    <div className="col-12 col-md-4 px-md-2">
                        <div className="form-group mb-3">
                            <label htmlFor="zip">
                                {getText('msg_d9d220ab2925')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                id="zip"
                                name="zip"
                                className={`form-control${errors.zip ? ' is-invalid' : ''}`}
                                value={formData.address.zip}
                                onChange={handleAddressChange}
                                placeholder={getText('msg_66746cfa9eac')}
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-4 ps-md-2">
                        <CountryDropdown
                            selectedCountry={formData.address.country}
                            onCountryChange={handleCountryChange}
                            inlineLabel={false}
                            isRequired
                            className={errors.country ? 'is-invalid' : ''}
                        />
                    </div>

                    <div className="col-12">
                        <div className="form-group mb-3">
                            <label htmlFor="note">
                                {getText('msg_70440046a3dc')}
                            </label>
                            <textarea
                                id="note"
                                name="note"
                                className="form-control"
                                rows={4}
                                value={formData.note}
                                onChange={handleNoteChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

BillingBillDetailTab.displayName = 'BillingBillDetailTab';

export default BillingBillDetailTab;
